import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { cartAPI } from "../services/api";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";

interface CartItem {
  id: string;
  product_id: string;
  product_name: string;
  product_image?: string;
  size: string;
  color?: string;
  quantity: number;
  price_at_add: number;
  subtotal: number;
}

interface Cart {
  id: string;
  items: CartItem[];
  total: number;
  item_count: number;
}

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  addToCart: (productId: string, size: string, quantity?: number, color?: string) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      refreshCart();
    } else {
      setCart(null);
    }
  }, [isAuthenticated]);

  const refreshCart = async () => {
    try {
      const res = await cartAPI.get();
      setCart(res.data);
    } catch {
      setCart(null);
    }
  };

  const addToCart = async (productId: string, size: string, quantity = 1, color?: string) => {
    setIsLoading(true);
    try {
      await cartAPI.addItem({ product_id: productId, size, quantity, color });
      await refreshCart();
      toast.success("Added to cart!");
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (itemId: string) => {
    setIsLoading(true);
    try {
      await cartAPI.removeItem(itemId);
      await refreshCart();
      toast.success("Removed from cart");
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    setIsLoading(true);
    try {
      if (quantity === 0) {
        await cartAPI.removeItem(itemId);
      } else {
        await cartAPI.updateItem(itemId, { quantity });
      }
      await refreshCart();
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    setIsLoading(true);
    try {
      await cartAPI.clear();
      setCart(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{ cart, isLoading, addToCart, removeFromCart, updateQuantity, clearCart, refreshCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
